import _ from 'lodash'

import { styled } from 'styled-components'
import {
  Json,
  Panels,
  Link,
  Modal,
  Bar,
  LegacyPage,
  Input,
  Breadcrumb,
  Select,
} from 'peach/components'
import { getName } from 'peach/helpers'
import { Switch, Route, Redirect, useRouteMatch, useParams } from 'peach/router'
import { CompanyPermissionSelect } from 'peach/scopes/company'
import {
  useCompanyRoleGetAll,
  useCompanyRoleUpdate,
  useCompanyRolePermissionsElevate,
  useCompanyRolePermissionsRevoke,
} from 'core/types'

import CompanyPermission from './CompanyPermission' // todo: rename to CompanyRolePermission
import { addToast } from 'core/stores'
import { Clickable } from 'core/components'

const CompanyRole = () => {
  const { url, path } = useRouteMatch()
  const { companyId, roleId } = useParams<{
    companyId?: string
    roleId?: string
  }>()

  const { data: roles, refetch: refetchCompanyRoles } = useCompanyRoleGetAll({
    companyId,
    query: { limit: 100 },
  })

  const { mutateAsync: updateRole } = useCompanyRoleUpdate({
    companyId,
    roleId,
  })
  const { mutateAsync: addPermission } = useCompanyRolePermissionsElevate({
    companyId,
    roleId,
  })
  const { mutateAsync: revokePermissions } = useCompanyRolePermissionsRevoke({
    companyId,
    roleId,
  })

  const role = _.find(roles, { id: roleId })
  const { permissions, ...rest } = role || {}

  const sortedPermissions = _.sortBy(permissions, 'action')

  const permissionItems = _.map(sortedPermissions, (perm) => (
    <StyledListItem key={perm.id}>
      <Link to={`${url}/permissions/${perm.id}`}>{perm.action},</Link>
    </StyledListItem>
  ))

  const handleCopyPermissions = async () => {
    const actions = _.map(permissions, 'action').sort()
    void navigator.clipboard
      .writeText(actions.join(',\n'))
      .then(() =>
        addToast(
          `Copied ${actions.length} permissions to clipboard`,
          'success',
        ),
      )
      .catch(() => addToast('Failed to copy permissions to clipboard', 'error'))
  }

  const name = getName(role)

  return (
    <>
      <Modal
        initialValue={role}
        onSubmit={async (values) => {
          await updateRole(values)
          await refetchCompanyRoles()
        }}
        title='Edit Role'
      >
        <Input formKey='alias' />
      </Modal>

      <Modal
        onSubmit={async (values) => {
          await addPermission(values)
          await refetchCompanyRoles()
        }}
        title='Add Permissions'
      >
        <CompanyPermissionSelect
          formKey='actions'
          multi
          omitPermissions={permissions}
        />
      </Modal>

      <Modal
        onSubmit={async (values) => {
          await revokePermissions(undefined, {
            query: { actions: values?.actions ?? [] },
          })
          await refetchCompanyRoles()
        }}
        title='Remove Permissions'
      >
        <Select
          formKey='actions'
          multi
          options={_.map(permissions, 'action')}
          searchable
        />
      </Modal>

      <Breadcrumb title={name} to={url} />
      <Switch>
        <Route exact path={path}>
          <Panels header={<Bar title={name} />}>
            <LegacyPage.Full>
              <Json.Table json={rest} title='Role' />
              <PermissionsTable>
                <thead>
                  <PermissionsTableRow>
                    <PermissionsTableHeader colSpan={2}>
                      <Clickable
                        onClick={handleCopyPermissions}
                        variant='primary'
                      >
                        Copy Permissions
                      </Clickable>
                    </PermissionsTableHeader>
                  </PermissionsTableRow>
                </thead>
                <tbody>
                  <PermissionsTableRow>
                    <PermissionsTableCell $width='121px'>
                      Permissions
                    </PermissionsTableCell>
                    <PermissionsTableCell>
                      <StyledList>{permissionItems}</StyledList>
                    </PermissionsTableCell>
                  </PermissionsTableRow>
                </tbody>
              </PermissionsTable>
            </LegacyPage.Full>
          </Panels>
        </Route>
        <Route crumb path={`${path}/permissions`}>
          <Route exact path={`${path}/permissions`}>
            <Redirect to={url} />
          </Route>
          <Route path={`${path}/permissions/:permissionId`}>
            <CompanyPermission />
          </Route>
        </Route>
        <Redirect to={url} />
      </Switch>
    </>
  )
}

export default CompanyRole

const PermissionsTable = styled.table`
  margin-top: 24px;
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
`

const PermissionsTableHeader = styled.th`
  border: 1px solid ${(p) => p.theme.border};
  background-color: ${(p) => p.theme.offset};
  padding: 8px;
  text-align: right;

  & > * {
    margin-left: auto;
  }
`

const PermissionsTableRow = styled.tr`
  background-color: ${(p) => p.theme.background};
`

const PermissionsTableCell = styled.td<{ $width?: string }>`
  border: 1px solid ${(p) => p.theme.border};
  padding: 8px;
  width: ${(p) => p.$width || 'auto'};
  vertical-align: top;
  color: ${(p) => p.theme.text};
`

const StyledList = styled.ul`
  padding-left: 0;
  list-style-type: none;
  color: ${(p) => p.theme.labelText} !important;
`

const StyledListItem = styled.li`
  margin-bottom: 8px;
`
